import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, Cards } from "@phosphor-icons/react";

const empty = { name: "", subject: "", html_content: "" };

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteId, setDeleteId] = useState(null);

  const load = () =>
    api
      .get("/templates")
      .then((r) => setTemplates(r.data))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, subject: t.subject, html_content: t.html_content });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editing) {
        const res = await api.put(`/templates/${editing}`, form);
        setTemplates((prev) => prev.map((t) => (t.id === editing ? res.data : t)));
        toast.success("Template updated");
      } else {
        const res = await api.post("/templates", form);
        setTemplates((prev) => [res.data, ...prev]);
        toast.success("Template created");
      }
      setOpen(false);
    } catch {
      toast.error("Failed to save template");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/templates/${deleteId}`);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteId));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 md:py-14 md:px-12">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Templates</h1>
          <p className="text-muted-foreground mt-2">Reusable email designs, ready to send.</p>
        </div>
        <Button data-testid="new-template-btn" onClick={openNew} className="rounded-full shrink-0">
          <Plus size={17} className="mr-2" /> New template
        </Button>
      </header>

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="border border-border rounded-md bg-card p-10 text-center" data-testid="templates-empty">
          <img
            src="https://images.unsplash.com/photo-1601642964568-1917224f4e4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxjYWxtJTIwbWluaW1hbGlzdCUyMHdvcmtzcGFjZSUyMGRlc2t8ZW58MHx8fHwxNzg2NTgxMDA3fDA&ixlib=rb-4.1.0&q=85"
            alt="No templates"
            className="w-full max-w-sm mx-auto rounded-md object-cover h-52 mb-6 border border-border"
          />
          <p className="font-display text-2xl mb-1">No templates yet</p>
          <p className="text-muted-foreground mb-5">Save an email design once and reuse it forever.</p>
          <Button onClick={openNew} className="rounded-full" data-testid="empty-new-template-btn">
            <Plus size={17} className="mr-2" /> Create your first template
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`template-card-${t.id}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.3 }}
              className="group border border-border rounded-md bg-card p-5 flex flex-col hover:border-primary/50 transition-colors duration-200"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="h-8 w-8 rounded bg-secondary flex items-center justify-center shrink-0">
                  <Cards size={17} className="text-primary" />
                </div>
                <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200">
                  <button
                    data-testid={`edit-template-${t.id}`}
                    onClick={() => openEdit(t)}
                    className="p-1.5 rounded hover:bg-secondary transition-colors"
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    data-testid={`delete-template-${t.id}`}
                    onClick={() => setDeleteId(t.id)}
                    className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              <p className="font-semibold truncate">{t.name}</p>
              <p className="text-sm text-muted-foreground truncate mt-0.5">{t.subject || "No subject"}</p>
              <p className="text-sm text-muted-foreground mt-3 line-clamp-3 flex-1">
                {t.html_content || "Empty body"}
              </p>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit template" : "New template"}
            </DialogTitle>
            <DialogDescription>Save a subject and body you can reuse when composing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Name</Label>
              <Input
                data-testid="tf-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Weekly newsletter"
                className="bg-background"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Subject</Label>
              <Input
                data-testid="tf-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Subject line"
                className="bg-background"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-2 block">Body</Label>
              <Textarea
                data-testid="tf-body"
                value={form.html_content}
                onChange={(e) => setForm({ ...form, html_content: e.target.value })}
                placeholder="Message body…"
                className="bg-background min-h-[160px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="tf-cancel">
              Cancel
            </Button>
            <Button onClick={save} data-testid="tf-save" className="rounded-full">
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-template">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-template" className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
